import { Button, ButtonVariation } from '../../../components/Button';
import { ChevronDirection, ChevronIcon } from '../../../components/Icon';

interface ApprovalDetailsActionsBarProps {
  canApprove: boolean;
  canDeclineAndDelete: boolean;
  canReturnForEdits: boolean;
  canRevert: boolean;
  /** FN approver only: whether the Send to FNMS action is available/enabled. */
  canSendToFnms?: boolean;
  /**
   * ACP-2421: the application is parked after a failed FNMS submission. The same slot becomes
   * Retry rather than adding a second button — a fresh send from this state would bypass the
   * dead-letter replay, so only one of the two is ever the correct action.
   */
  isFnmsRetry?: boolean;
  onApprove: () => void;
  onBackToManageApprovals: () => void;
  onDeclineAndDelete: () => void;
  onReturnForEdits: () => void;
  onRevert: () => void;
  /** FN approver only: opens the "Create Foreign National Visit?" confirmation. */
  onSendToFnms?: () => void;
  /** When true, render the FN approver action set (adds Send to FNMS as the primary CTA). */
  showSendToFnms?: boolean;
}

function ApprovalDetailsActionsBar({
  canApprove,
  canDeclineAndDelete,
  canReturnForEdits,
  canRevert,
  canSendToFnms = false,
  isFnmsRetry = false,
  onApprove,
  onBackToManageApprovals,
  onDeclineAndDelete,
  onReturnForEdits,
  onRevert,
  onSendToFnms,
  showSendToFnms = false,
}: ApprovalDetailsActionsBarProps) {
  return (
    <div
      className='approval-details-action-bar'
      data-testid='approval-details-action-bar'>
      <Button
        bordered
        isAutoWidth
        aria-label='Back to Manage Approvals'
        className='approval-details-action-bar__button approval-details-action-bar__button--back'
        data-testid='approval-details-action-bar-back'
        type='button'
        variation={ButtonVariation.GHOST}
        onClick={onBackToManageApprovals}>
        <ChevronIcon
          className='approval-details-action-bar__button-icon'
          direction={ChevronDirection.LEFT}
          size={16}
        />
        <span>Back to Manage Approvals</span>
      </Button>
      <div
        className='approval-details-action-bar__review-actions'
        data-testid='approval-details-action-bar-review-actions'>
        {showSendToFnms && (
          <Button
            isAutoWidth
            aria-label={isFnmsRetry ? 'Retry Send to FNMS' : 'Send to FNMS'}
            className='approval-details-action-bar__button approval-details-action-bar__button--primary'
            data-testid='approval-details-action-bar-send-to-fnms'
            disabled={!canSendToFnms}
            type='button'
            variation={ButtonVariation.SOLID}
            onClick={onSendToFnms}>
            {isFnmsRetry ? 'Retry Send to FNMS' : 'Send to FNMS'}
          </Button>
        )}
        <Button
          isAutoWidth
          aria-label='Return for Edits'
          className='approval-details-action-bar__button'
          data-testid='approval-details-action-bar-return-for-edits'
          disabled={!canReturnForEdits}
          type='button'
          variation={ButtonVariation.SOLID}
          onClick={onReturnForEdits}>
          Return for Edits
        </Button>
        <Button
          isAutoWidth
          aria-label='Clear All'
          className='approval-details-action-bar__button'
          data-testid='approval-details-action-bar-revert'
          disabled={!canRevert}
          type='button'
          variation={ButtonVariation.SOLID}
          onClick={onRevert}>
          Clear All
        </Button>
        <Button
          isAutoWidth
          aria-label='Decline and Delete'
          className='approval-details-action-bar__button'
          data-testid='approval-details-action-bar-decline-delete'
          disabled={!canDeclineAndDelete}
          type='button'
          variation={ButtonVariation.SOLID}
          onClick={onDeclineAndDelete}>
          Decline and Delete
        </Button>
        <Button
          isAutoWidth
          aria-label='Approve'
          className='approval-details-action-bar__button'
          data-testid='approval-details-action-bar-approve'
          disabled={!canApprove}
          type='button'
          variation={ButtonVariation.SOLID}
          onClick={onApprove}>
          Approve
        </Button>
      </div>
    </div>
  );
}

export default ApprovalDetailsActionsBar;
